import { useState } from 'react';
import clsx from 'clsx';
import {
  updateOrderFields,
  OrderConflictError,
  type OrderFactuur,
} from '../../lib/orders';

interface EditInvoiceNameDialogProps {
  orderId: string;
  /** Standaardnaam die op de factuur komt als er geen afwijkende naam is gezet. */
  accountName: string;
  current: OrderFactuur;
  expectedLastModifiedDate: string | null;
  onClose: () => void;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

/**
 * Bewerkt de factuurnaam-overschrijving op een werk order:
 *   - "Standaard" → Andere_naam_op_factuur__c = false (factuur gebruikt accountnaam)
 *   - "Afwijkende naam" → Andere_naam_op_factuur__c = true + Naam_op_Factuur__c
 *   - Opmerkingen op factuur (Opmerkingen_op_factuur__c) altijd bewerkbaar
 *
 * Stuurt via de generieke /api/orders/update-fields PATCH (invoice-blok).
 */
export function EditInvoiceNameDialog({
  orderId,
  accountName,
  current,
  expectedLastModifiedDate,
  onClose,
  onSaved,
}: EditInvoiceNameDialogProps) {
  const [mode, setMode] = useState<'standaard' | 'afwijkend'>(
    current.andereNaam ? 'afwijkend' : 'standaard'
  );
  const [naam, setNaam] = useState(current.naam);
  const [opmerkingen, setOpmerkingen] = useState(current.opmerkingen);
  const [email, setEmail] = useState(current.email);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const desiredAndereNaam = mode === 'afwijkend';
  const trimmedNaam = naam.trim();
  const trimmedOpmerkingen = opmerkingen.trim();
  const trimmedEmail = email.trim();

  const naamChanged = desiredAndereNaam && trimmedNaam !== current.naam;
  const andereNaamChanged = desiredAndereNaam !== current.andereNaam;
  const opmerkingenChanged = trimmedOpmerkingen !== current.opmerkingen;
  const emailChanged = trimmedEmail !== current.email;
  const changed = andereNaamChanged || naamChanged || opmerkingenChanged || emailChanged;

  const emailInvalid = trimmedEmail.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  // Afwijkende naam gekozen maar leeg → niet opslaanbaar. Idem ongeldige e-mail.
  const invalid = (desiredAndereNaam && trimmedNaam.length === 0) || emailInvalid;

  const handleSubmit = async () => {
    if (!changed) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const invoice: NonNullable<Parameters<typeof updateOrderFields>[1]['invoice']> = {
        andereNaam: desiredAndereNaam,
        opmerkingen: trimmedOpmerkingen,
      };
      // Naam alleen meesturen als 'afwijkend' — bij 'standaard' laten we de
      // opgeslagen naam staan (handig als men later terugschakelt).
      if (desiredAndereNaam) invoice.naam = trimmedNaam;
      // E-mail alleen meesturen bij wijziging (leeg = wissen → default-adres).
      if (emailChanged) invoice.email = trimmedEmail;

      const res = await updateOrderFields(orderId, { invoice }, expectedLastModifiedDate ?? undefined);
      onSaved(res.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Naam op factuur wijzigen mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Naam op factuur</h2>
          <p className="text-xs text-slate-500 mt-1">
            Standaard staat de accountnaam op de factuur. Zet een afwijkende naam als dat nodig is.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <label
            className={clsx(
              'flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer',
              mode === 'standaard'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:bg-slate-50'
            )}
          >
            <input
              type="radio"
              name="factuurnaam-modus"
              className="mt-1"
              checked={mode === 'standaard'}
              onChange={() => setMode('standaard')}
              disabled={submitting}
            />
            <span className="min-w-0">
              <span className="text-sm font-medium text-slate-900 block">Standaard — accountnaam</span>
              <span className="text-xs text-slate-500 block truncate">
                {accountName || '(geen accountnaam bekend)'}
              </span>
            </span>
          </label>

          <label
            className={clsx(
              'flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer',
              mode === 'afwijkend'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:bg-slate-50'
            )}
          >
            <input
              type="radio"
              name="factuurnaam-modus"
              className="mt-1"
              checked={mode === 'afwijkend'}
              onChange={() => setMode('afwijkend')}
              disabled={submitting}
            />
            <span className="min-w-0 flex-1">
              <span className="text-sm font-medium text-slate-900 block">Afwijkende naam</span>
              <input
                type="text"
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
                onFocus={() => setMode('afwijkend')}
                placeholder="Naam zoals op de factuur"
                maxLength={120}
                disabled={submitting}
                className="mt-1.5 w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </span>
          </label>

          <div className="pt-1">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">
              Factuur-e-mailadres
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Standaard: e-mail van klant/contact"
              maxLength={240}
              disabled={submitting}
              className={clsx(
                'w-full text-sm rounded border px-2 py-1.5 focus:outline-none focus:ring-1',
                emailInvalid
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-500'
              )}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Stel dit in vóór de factuur wordt aangemaakt, anders vertrekt de mail naar het standaardadres. Leeg = terug naar standaard.
            </p>
          </div>

          <div className="pt-1">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">
              Opmerkingen op factuur
            </label>
            <textarea
              value={opmerkingen}
              onChange={(e) => setOpmerkingen(e.target.value)}
              placeholder="Optionele opmerkingenregel op de factuur"
              maxLength={255}
              rows={2}
              disabled={submitting}
              className="w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || invalid || !changed}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || invalid || !changed
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
